<script setup>
import { reactive, ref, onMounted, watch, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import LoadingIcon from "@/components/LoadingIcon.vue";
import ChatQuestionTemplateInput from "@/components/ChatQuestionTemplateInput.vue"
import { chatsApi, adminApi, templatesApi } from "@/api";
import { ChatQuestionTemplates, ChatQuestionTemplatesResponse } from "@/store/modules/chat";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const chatId = route.params.chat_id;
const source = reactive([]);
const selected = ref([]);
const rawGroups = reactive({ data: [] });
let temRes = undefined;
const text = ref("");
const chat = reactive({ data: [] });
const isLoading = ref(false);
const chat_question_template_list = ref([]);
let aiModels = [];
const selected_resource_type = ref("");
const templates_lsit = reactive({ data: [] });
const selected_template_id = ref(0);
const showTemplat = computed(() => {
  return selected_resource_type.value === "spread_sheet";
});
const show_template_err = ref(false);

onMounted(async () => {
  // 認可をチェック
  try {
    const authResp = await chatsApi.check_authorization();
    console.log(authResp)
    if (authResp.message !== "ok") {
      // 認可が"ok"でない場合、エラーページへリダイレクト
      return router.push(`/admin_error`);
    }
  } catch (error) {
    return router.push(`/admin_error`);
    // ここでエラーページにリダイレクトするか、エラーメッセージを表示する処理を追加
  }
  const resp = await chatsApi.getById(chatId);
  chat.data = resp?.data;
  selected.value = resp?.data.groups.map(item => item.group_name);
  selected_resource_type.value = resp?.data.resource_type || "spread_sheet"; // デフォルト値を設定
  setTemplate();
  await fetchAiModels(chat);
  const resp_group = await adminApi.getAllGroups();
  source.push(...resp_group?.data.map((group) => group.group_name));
  rawGroups.data = resp_group.data;

  const resp_template = await templatesApi.getAll();
  templates_lsit.data = resp_template?.data;

  await getChatsTemplateByChatId(chatId);
});

watch(text, (newText) => {
  if (selected.value.indexOf(newText) == -1 && newText != "") {
    selected.value.push(newText);
    text.value = "";
  }
});

const del = (index) => {
  if (text.value.length === 0) {
    selected.value.splice(index, 1);
  }
};

const setTemplate = async () => {
  await chatsApi.getTemplateById(chatId).then(response => {
    temRes = response;
    response?.data.forEach(ChatQuestionTemplate => {
      ChatQuestionTemplate.on_check = false;
      chat_question_template_list.value.push(ChatQuestionTemplate);
    });
  }).catch(() => {
    //const first_chat_question_template = new ChatQuestionTemplates(chatId);
    //chat_question_template_list.push(first_chat_question_template);
  });
}

const selectedGroupId = () => {
  //if (selected.value.includes("ALL")) {
  //   return rawGroups.data.filter(group => group.group_name == "ALL").map(group => group.id);
  //}
  return rawGroups.data.filter(group => selected.value.includes(group.group_name)).map(group => group.id);
};

const handleReturn = () => {
  router.back();
};

const handleUpdate = async (e) => {
  if (!checkTemplete()) {
    return;
  }
  let nullTemp = false;
  chat_question_template_list.value.forEach(ChatQuestionTemplate => {
    if (!(ChatQuestionTemplate.question_title !== "" && ChatQuestionTemplate.question_text !== "")) {
      alert(t('ChatSettingView.alert_not_edit'));
      nullTemp = true;
      return
    }
  });
  e.preventDefault();
  if (!nullTemp) {
    await updateChat();
  }
};

const updateChat = async () => {
  const data = setResData();
  isLoading.value = true;
  try {
    await chatsApi.updateChat(chatId, data);
    await updateTemplates();
    await editChatsTemplate();
  } catch (error) {
    console.log(error);
    isLoading.value = false;
    alert(t('ChatSettingView.chat_setting_err_msg'));
    return;
  }
  isLoading.value = false;
  alert(t('ChatSettingView.chat_setting_ok_msg'));
};

const setResData = () => {
  const data = {
    chat_namespace: chat.data.chat_namespace,
    chat_title: chat.data.chat_title,
    system_prompt: chat.data.system_prompt,
    temperature: chat.data.temperature,
    ai_model_id: chat.data.ai_model_id,
    icon_img: chat.data.icon_img,
    memory_flag: chat.data.memory_flag,
    groups: selectedGroupId(),
    display_order: chat.data.display_order ? chat.data.display_order : null,
    resource_type: selected_resource_type.value  // 添加 resource_type，確保切換時後端同步更新
  }
  return data
};

const handleAddQuestionTemplate = () => {
  if (chat_question_template_list.value.length <= 10) {
    chat_question_template_list.value.push(new ChatQuestionTemplates(chatId));
  } else {
    alert(t('ChatSettingView.alert_max_10'));
  }
}

const handleDeleteTemplate = () => {
  // 削除前の状態をログ出力
  console.log('削除前のテンプレートリスト:', chat_question_template_list.value);
  
  // チェックされたアイテムを削除
  const filteredData = chat_question_template_list.value.filter(item => !item.on_check);
  
  // 配列を完全に置き換えてVueの反応性を確実にトリガー
  chat_question_template_list.value.length = 0;
  chat_question_template_list.value.push(...filteredData);
  
  // 削除後の状態をログ出力
  console.log('削除後のテンプレートリスト:', chat_question_template_list.value);
}

async function updateTemplates() {
  if (temRes == undefined && chat_question_template_list.value.length == 0) {
    return
  }
  if (chat_question_template_list.value.length == 0 || (
    chat_question_template_list.value[0].question_title == "" &&
    chat_question_template_list.value[0].question_text == "")
  ) {
    await chatsApi.deleteChatQuestionTemplates(chatId);
    return
  }

  let templates_lsit = []
  chat_question_template_list.value.forEach(template => {
    const template_response = new ChatQuestionTemplatesResponse(
      template.chat_id,
      template.question_title,
      template.question_text
    )
    templates_lsit.push(template_response)
  });
  await chatsApi.updateChatQuestionTemplates(templates_lsit);
}

const fetchAiModels = async (chat) => {
  const response = await chatsApi.getAiModels();
  console.log(response.data)
  aiModels = response.data;
  console.log(chat)
  console.log(aiModels)
  // aiModels から chat.ai_model_id と一致するものを探し、見つかればそのIDを設定
  const matchingModel = aiModels.find(model => model.id === chat.data.ai_model_id);
  console.log("matchingModel:" + matchingModel)
  console.log(chat)
  if (matchingModel) {
    chat.data.ai_model_id = matchingModel.id;
  } else if (aiModels.length > 0) {
    // 一致するものがない場合、デフォルトとして最初のモデルのIDを設定（オプション）
    chat.data.ai_model_id = aiModels[0].id;
  }
}

const editChatsTemplate = async () => {
  // チャットのリソースタイプが "resource" 且つ、選択されたリソースタイプが "spread_sheet" の場合、テンプレートを作成
  if (chat.data.resource_type === "resource" && selected_resource_type.value === "spread_sheet") {
    const data = {
      chat_id: chatId,
      cs_template_sheet_id: selected_template_id.value,
    };
    await templatesApi.createChatsTemplate(data);
  }

  // チャットのリソースタイプが "resource" 且つ、選択されたリソースタイプが "resource" の場合、何もしない
  if (chat.data.resource_type === "resource" && selected_resource_type.value === "resource") {
    return;
  }

  // チャットのリソースタイプが "spread_sheet" 且つ、選択されたリソースタイプが "resource" の場合、テンプレートを削除
  if (chat.data.resource_type === "spread_sheet" && selected_resource_type.value === "resource") {
    await templatesApi.deleteChatsTemplate(chatId);
  }

  // チャットのリソースタイプが "spread_sheet" 且つ、選択されたリソースタイプが "spread_sheet" の場合テンプレートを更新
  if (chat.data.resource_type === "spread_sheet" && selected_resource_type.value === "spread_sheet") {
    const data = {
      chat_id: chatId,
      cs_template_sheet_id: selected_template_id.value,
      resource_type: selected_resource_type.value
    };
    await templatesApi.updateChatsTemplate(data);
  }
}

const getChatsTemplateByChatId = async (chatId) => {
  if (chat.data.resource_type !== "spread_sheet") {
    // チャットのリソースタイプがスプレッドシートでない場合は何もしない
    return;
  }
  const resp_chats_template = await templatesApi.getChatsTemplateByChatId(chatId);
  selected_template_id.value = resp_chats_template?.data;
};

const checkTemplete = () => {
  console.log("selected_resource_type.value:", selected_resource_type.value);
  console.log("templates_lsit.data:", templates_lsit.data);

  if ((selected_resource_type.value === "spread_sheet" || selected_resource_type.value === "")
    && (templates_lsit.data.length === 0 || !templates_lsit.data)
  ) {
    show_template_err.value = true;
    return false;
  } else {
    show_template_err.value = false;
    return true;
  }
};
</script>

<template>
  <!-- チャットボット設定画面 -->
  <LoadingIcon :is-loading="isLoading"></LoadingIcon>
  <br>
  <h4>{{ $t('ChatSettingView.h4') }}</h4>
  <br>
  <label for="chat_namespace" class="form-label">
    {{ $t('ChatSettingView.chat_namespace') }} <span>{{ chatId }}</span>
  </label>
  <br>
  <label for="chat_title" class="form-label">{{ $t('ChatSettingView.chat_title') }} </label>
  <div class="input-group mb-3">
    <input type="text" class="form-control shadow-sm" id="chat_title" v-model="chat.data.chat_title">
  </div>
  <label for="system_prompt" class="form-label">{{ $t('ChatSettingView.system_prompt') }} </label>
  <div class="input-group mb-3">
    <textarea class="form-control shadow-sm" id="system_prompt" v-model="chat.data.system_prompt" rows="5"
      cols="50"></textarea>
  </div>
  <label for="temperature" class="form-label">
    {{ $t('ChatSettingView.temperature') }} <span>{{ chat.data.temperature }}</span>
  </label>
  <div class="input-group mb-3">
    <input type="range" class="form-range" v-model.number="chat.data.temperature" min="0" max="1" step="0.01"
      id="temperature">
  </div>
  <label for="roles" class="form-label">{{ $t('ChatSettingView.group') }}</label>
  <div class="mb-3">
    <div class="tags rounded-pill d-flex flex-wrap mb-3 shadow-sm" v-for="(item, index) in selected" :key="index">
      <span class="content">{{ item }}</span>
      <span class="btn-animation del" @click="del(index)">&times;</span>
    </div>

    <select class="form-select shadow-sm" aria-label="Default select example" v-model="text">
      <option selected></option>
      <option v-for="(item, index) in source" :key="index" :value="item">{{ item }}</option>
    </select>
  </div>

  <br>
  <div class="mb-3">
    <label class="form-label">{{ $t('ChatSettingView.template_message') }}</label>
    <div class="d-grid gap-2 d-md-flex justify-content-md-end">
      <button class="btn btn-primary d-md-flex justify-content-md-end shadow" @click="handleAddQuestionTemplate"
        style="padding: 0 50px; margin: 10px;">{{ $t('ChatSettingView.add') }}
      </button>
      <button class="btn btn-primary d-md-flex justify-content-md-end shadow" @click="handleDeleteTemplate"
        style="padding: 0 50px; margin: 10px;">{{ $t('ChatSettingView.del') }}
      </button>
    </div>
  </div>

  <!-- チャットテンプレートテーブル -->
  <table class="table table-hover" id="chat_question_templates">
    <thead>
      <tr>
        <th scope="col">#</th>
        <th scope="col">{{ $t('ChatSettingView.title') }}</th>
        <th scope="col">{{ $t('ChatSettingView.msg') }}</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(tem, index) in chat_question_template_list" :key="index">
        <td><input class="form-check-input " type="checkbox" v-model="tem.on_check" aria-label="..."></td>
        <td>
          <ChatQuestionTemplateInput :text="tem.question_title" @getInput="(value) => tem.question_title = value" />
        </td>
        <td>
          <ChatQuestionTemplateInput :text="tem.question_text" @getInput="(value) => tem.question_text = value" />
        </td>
      </tr>
    </tbody>
  </table>
  <br>
  <br>
  <label for="model" class="form-label">{{ $t('ChatSettingView.model') }} </label>
  <div class="mb-3">
    <select class="form-select shadow-sm" id="model" v-model="chat.data.ai_model_id">
      <option v-for="model in aiModels" :value="model.id" v-bind:key="model.id">{{ model.model_name }}</option>
    </select>
  </div>
  <br>
  <label for="model" class="form-label">{{ $t('ChatSettingView.resource_type') }}</label>
  <div class="mb-3">
    <select class="form-select shadow-sm" id="model" v-model="selected_resource_type">
      <option value="resource">{{ $t('ChatSettingView.resource_rag') }}</option>
      <option value="spread_sheet">{{ $t('ChatSettingView.template') }}</option>
    </select>
  </div>
  <label for="model" class="form-label" v-if="showTemplat">{{ $t('ChatSettingView.template') }}</label>
  <div class="mb-3" v-if="showTemplat">
    <select class="form-select shadow-sm" id="model" v-model="selected_template_id">
      <option v-for="template in templates_lsit.data" :value="template.id" v-bind:key="template.id">
        {{ template.template_name }}
      </option>
    </select>
  </div>
  <div class="alert alert-danger" role="alert" v-if="show_template_err">
    {{ $t('ChatSettingView.template_err') }}
  </div>

  <br>
  <div class="d-grid gap-2 d-md-flex justify-content-md-end">
    <button class="btn btn-primary shadow" type="button" @click="handleUpdate($event)">
      {{ $t('ChatSettingView.update') }}
    </button>
    <button class="btn btn-primary shadow" type="button" @click="handleReturn">
      {{ $t('ChatSettingView.handle_return') }}
    </button>
  </div>

</template>

<style scoped>
label {
  margin-right: 2rem;
}

.settings-item {
  margin: 3rem 0;
}

.settings-field {
  margin: 10px 5px;
  padding: 0 1rem;
}

input {
  width: 40%;
}

.settings {
  width: 70%;
  margin: 0 auto;
}

.settings-buttons {
  display: flex;
  justify-content: center;
  gap: 50px;
}

.settings-buttons button {
  border: none;
  width: 20%;
  border-radius: 0;
}

.btn-animation:hover {
  cursor: pointer;
}

.tags {
  position: relative;
  float: left;
  color: #000000;
  line-height: 28px;
  margin: 0 4px 4px 0;
  padding: 0 22px 0 10px;
}

.tags .content {
  line-height: 28px;
}

.tags .del {
  width: 22px;
  height: 28px;
  text-align: center;
  cursor: pointer;
  position: absolute;
  top: -1px;
  right: 0;
}

.tags {
  background-color: #e0e0e0;
  border: 1px solid rgb(54, 54, 54);
  cursor: default;
}
</style>
